import { RMSvgIconProps } from 'components/customComponents/CommonComponents/RMSvgIcon';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTabBackButtonState } from './backButtonHandler';
import { TabPanel, useTabsHeadless } from './headless';
import TabSelector from './tabSelector';
import { HeaderTab } from './headerTab';
import { EPanelHeaderVariant } from 'components/sidePanel/baseSidePanel';

export interface ITabItem {
  title?: string;
  eventKey?: string;
  id?: string | number;
  iconProps?: RMSvgIconProps;
  customTabSelectionItem?: React.ReactNode;
  tabSelectionItemContainerStyle?: React.CSSProperties;
  content: React.ReactNode;
  tabPanelContainerStyle?: React.CSSProperties;
  disabled?: boolean;
  externalKey?: string;
  width?: number;
  height?: string;
  tabClassName?: string;
}

const DEFAULT_CONTENT_WIDTH = 30;

/**
 * It's kind of an anti-Pattern, but since we are memoizing it and it's best for this use case.
 * But, we should avoid returning Elements from hooks.
 */

const useTabs = (
  items: ITabItem[],
  defaultTab: any = 0,
  onTabSelectionChange?: (...args) => void,
  isButtonsDisabled = false,
  useExternalKeys = false,
  headerVariant = EPanelHeaderVariant.Default,
): Array<React.ReactNode> => {
  const tabKeys: number[] = useMemo(() => [...Array(items.length).keys()], [items]);
  const [selectedTab, setSelectedTab] = useTabsHeadless(
    tabKeys,
    defaultTab,
    useExternalKeys,
  );
  const [, setHandleOnTabBackPress] = useTabBackButtonState();

  const _onTabSelectionChange = useCallback(
    (_ix) => {
      setSelectedTab((prv) => {
        const flag =
          _ix === prv && headerVariant === EPanelHeaderVariant.Default ? undefined : _ix;
        if (onTabSelectionChange) {
          onTabSelectionChange(flag === undefined ? null : flag);
        }
        return flag;
      });
    },
    [onTabSelectionChange, selectedTab],
  );

  useEffect(() => {
    if (defaultTab === -1 && onTabSelectionChange) {
      onTabSelectionChange();

      return;
    }

    if (defaultTab === null) {
      setSelectedTab(undefined);
      return;
    }

    if (onTabSelectionChange) {
      setSelectedTab(defaultTab);
    }
  }, [items, defaultTab]);

  const handleIsActive = (key: any) => selectedTab === key;

  const tabSelectors: React.ReactNode[] = useMemo(
    () =>
      items.map((item, _ix) => {
        return headerVariant === EPanelHeaderVariant.TabsHeader ? (
          <HeaderTab
            isActive={handleIsActive(useExternalKeys ? item.externalKey : _ix)}
            onClick={() =>
              _onTabSelectionChange(useExternalKeys ? item.externalKey : _ix)
            }
            disabled={isButtonsDisabled || item.disabled}
            title={item.title}
          />
        ) : (
          <TabSelector
            key={`tabSelector-${useExternalKeys ? item.externalKey : _ix}`}
            isDisabled={isButtonsDisabled || item.disabled}
            onClick={() =>
              _onTabSelectionChange(useExternalKeys ? item.externalKey : _ix)
            }
            isActive={handleIsActive(useExternalKeys ? item.externalKey : _ix)}
            iconProps={item.iconProps}
            customContainerStyle={item.tabSelectionItemContainerStyle}
            tooltip={item.title}
            isStartTab={_ix === 0 && items.length > 1}
            isEndTab={_ix === items.length - 1 && items.length > 1}
          >
            {item.customTabSelectionItem || null}
          </TabSelector>
        );
      }),
    [items, selectedTab, _onTabSelectionChange],
  );

  const tabPanels: React.ReactNode[] = useMemo(
    () =>
      selectedTab !== undefined
        ? items.map((item, _ix) => (
            <TabPanel
              id={`tab-tabpanel-${_ix}`}
              hidden={!handleIsActive(useExternalKeys ? item.externalKey : _ix)}
              key={`tabPanel-${_ix}`}
              containerStyle={item.tabPanelContainerStyle}
              unmount="never"
              width={item.width || DEFAULT_CONTENT_WIDTH}
            >
              {item.content}
            </TabPanel>
          ))
        : [],
    [selectedTab, items],
  );

  const selectedTabpanel: React.ReactNode = useMemo(() => {
    let tabIpanel: React.ReactNode = undefined;
    for (const [_ix, item] of items.entries()) {
      if (handleIsActive(useExternalKeys ? item.externalKey : _ix)) {
        tabIpanel = (
          <TabPanel
            id={`tab-selectedtabpanel-${_ix}`}
            hidden={!handleIsActive(useExternalKeys ? item.externalKey : _ix)}
            key={`tabPanel-${_ix}`}
            containerStyle={item.tabPanelContainerStyle}
            unmount="never"
            width={item.width || DEFAULT_CONTENT_WIDTH}
          >
            {item.content}
          </TabPanel>
        );
        break;
      }
    }
    return tabIpanel;
  }, [selectedTab, items]);

  if (!tabPanels.length) {
    setHandleOnTabBackPress(null);
  }

  return [tabSelectors, tabPanels, selectedTabpanel];
};

export { useTabs };
